/**
 * Admin Installation Connection RPC definitions
 *
 * CRUD endpoints for `installation_connections` rows backing the
 * Connect & manage UI. Requires admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import {
  ConnectionStatus,
  ConnectionType,
  GoogleDriveAccess,
  InstallationConnection,
} from "@fm/db/models/InstallationConnection";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import {
  CryptoError,
  DatabaseError,
  Forbidden,
  RecordNotFoundError,
  Unauthorized,
  ValidationError,
} from "../errors.ts";
import {
  WhatsappCancelPairingResponse,
  WhatsappPairingInput,
  WhatsappPairingRpcError,
  WhatsappPollPairingResponse,
  WhatsappStartPairingResponse,
} from "../whatsapp-pairing.rpc.ts";
import { OAuthRpcError } from "./oauth-providers.rpc.ts";

// ============================================================================
// Provider catalog schemas
// ============================================================================

/**
 * Public-safe projection of an OAuth provider registry entry.
 *
 * Excludes server-only fields (env-var names, auth/token URLs) the UI
 * does not need — those stay private to the OAuth flow.
 *
 * @since 1.0.0
 */
export const OAuthProviderCatalogEntry = Schema.Struct({
  kind: Schema.Literal("oauth"),
  key: Schema.String,
  type: ConnectionType,
  scopes: Schema.Array(Schema.String),
  scopeCatalog: Schema.optional(
    Schema.Record({ key: Schema.String, value: Schema.Array(Schema.String) }),
  ),
});

/**
 * Public-safe projection of an API-key provider registry entry.
 *
 * @since 1.0.0
 */
export const ApiKeyProviderCatalogEntry = Schema.Struct({
  kind: Schema.Literal("apiKey"),
  key: Schema.String,
  type: ConnectionType,
});

/**
 * Public-safe projection of a pairing provider registry entry (e.g.
 * WhatsApp QR pairing). Like the API-key entry it carries no endpoints;
 * the UI renders a pairing affordance instead of a key-paste field.
 *
 * @since 1.0.0
 */
export const PairingProviderCatalogEntry = Schema.Struct({
  kind: Schema.Literal("pairing"),
  key: Schema.String,
  type: ConnectionType,
});

/**
 * Catalog entry — discriminated union over provider kind.
 *
 * @since 1.0.0
 */
export const ProviderCatalogEntry = Schema.Union(
  OAuthProviderCatalogEntry,
  ApiKeyProviderCatalogEntry,
  PairingProviderCatalogEntry,
);
export type ProviderCatalogEntry = Schema.Schema.Type<typeof ProviderCatalogEntry>;

/**
 * Provider-catalog response: list of entries, alphabetized by key.
 *
 * @since 1.0.0
 */
export const ProviderCatalogResponse = Schema.Array(ProviderCatalogEntry);

// ============================================================================
// Request / Response Schemas
// ============================================================================

/**
 * List connections for an installation.
 *
 * @since 1.0.0
 */
export const ListInstallationConnectionsInput = Schema.Struct({
  installationId: Schema.String,
});

/**
 * Idempotent upsert keyed on (installationId, provider, accountKey).
 *
 * `accountKey` defaults to the empty string for providers that have no
 * per-account concept (matches the table's column default and the
 * accessor's contract).
 *
 * @since 1.0.0
 */
export const UpsertInstallationConnectionInput = Schema.Struct({
  installationId: Schema.String,
  provider: Schema.NonEmptyTrimmedString,
  accountKey: Schema.optional(Schema.String),
  type: ConnectionType,
  status: Schema.optional(ConnectionStatus),
  accessToken: Schema.optional(Schema.NullOr(Schema.String)),
  refreshToken: Schema.optional(Schema.NullOr(Schema.String)),
  scopes: Schema.optional(Schema.NullOr(Schema.Array(Schema.String))),
  accountLabel: Schema.optional(Schema.NullOr(Schema.String)),
  metadata: Schema.optional(Schema.NullOr(Schema.Unknown)),
});

// ============================================================================
// Exchange-mail (email) connection schemas
// ============================================================================

/**
 * Non-secret IMAP account config for an exchange-mail connection. Mirrors the
 * `imap` subset of the `@fm/openclaw-exchange-mail` plugin `ConfigSchema` MINUS
 * the password — the password never rides in this struct, it is a top-level
 * secret input encrypted into `secretFields`.
 *
 * @since 1.0.0
 */
export const ExchangeMailImapConfig = Schema.Struct({
  host: Schema.NonEmptyTrimmedString,
  port: Schema.optional(Schema.Int),
  secure: Schema.optional(Schema.Boolean),
  user: Schema.NonEmptyTrimmedString,
});
export type ExchangeMailImapConfig = Schema.Schema.Type<typeof ExchangeMailImapConfig>;

/**
 * Non-secret SMTP account config for an exchange-mail connection. Mirrors the
 * `smtp` subset of the plugin `ConfigSchema` MINUS the password.
 *
 * @since 1.0.0
 */
export const ExchangeMailSmtpConfig = Schema.Struct({
  host: Schema.NonEmptyTrimmedString,
  port: Schema.optional(Schema.Int),
  secure: Schema.optional(Schema.Boolean),
  user: Schema.NonEmptyTrimmedString,
  from: Schema.NonEmptyTrimmedString,
});
export type ExchangeMailSmtpConfig = Schema.Schema.Type<typeof ExchangeMailSmtpConfig>;

/**
 * Non-secret outbound recipient policy for an exchange-mail connection. Mirrors
 * the plugin `ConfigSchema`'s `rules.outbound` block — the fail-closed allow/deny
 * lists the policy engine enforces. All fields optional; an omitted `mode`
 * defaults to `allowlist` (fail-closed) in the plugin.
 *
 * @since 1.0.0
 */
export const ExchangeMailOutboundRules = Schema.Struct({
  mode: Schema.optional(Schema.Literal("allowlist", "denylist", "open")),
  allowDomains: Schema.optional(Schema.Array(Schema.String)),
  allowAddresses: Schema.optional(Schema.Array(Schema.String)),
  denyDomains: Schema.optional(Schema.Array(Schema.String)),
  denyAddresses: Schema.optional(Schema.Array(Schema.String)),
  maxRecipientsPerMessage: Schema.optional(Schema.Int),
});
export type ExchangeMailOutboundRules = Schema.Schema.Type<typeof ExchangeMailOutboundRules>;

/**
 * Non-secret send rate limits for an exchange-mail connection. Mirrors the
 * plugin `ConfigSchema`'s `rules.rateLimit` block.
 *
 * @since 1.0.0
 */
export const ExchangeMailRateLimit = Schema.Struct({
  perAccountPerMinute: Schema.optional(Schema.Int),
  perAccountPerHour: Schema.optional(Schema.Int),
});
export type ExchangeMailRateLimit = Schema.Schema.Type<typeof ExchangeMailRateLimit>;

/**
 * Non-secret rules block (outbound policy + rate limits) for an exchange-mail
 * connection. Mirrors the plugin `ConfigSchema`'s `rules` block.
 *
 * @since 1.0.0
 */
export const ExchangeMailRules = Schema.Struct({
  outbound: Schema.optional(ExchangeMailOutboundRules),
  rateLimit: Schema.optional(ExchangeMailRateLimit),
});
export type ExchangeMailRules = Schema.Schema.Type<typeof ExchangeMailRules>;

/**
 * Non-secret telemetry preferences for an exchange-mail connection. Mirrors the
 * plugin `ConfigSchema`'s `telemetry` block — emits structured, no-PII events.
 *
 * @since 1.0.0
 */
export const ExchangeMailTelemetry = Schema.Struct({
  enabled: Schema.optional(Schema.Boolean),
  level: Schema.optional(Schema.Literal("off", "basic", "verbose")),
  includeRecipientDomains: Schema.optional(Schema.Boolean),
});
export type ExchangeMailTelemetry = Schema.Schema.Type<typeof ExchangeMailTelemetry>;

/**
 * Non-secret exchange-mail account config — the subset of the plugin's
 * `ConfigSchema` account block that is safe to persist in the connection row's
 * `metadata`. Passwords are excluded by construction (they are separate secret
 * inputs), so a valid value can never carry a credential.
 *
 * The optional `rules` (outbound policy + rate limits) and `telemetry` blocks
 * mirror the plugin `ConfigSchema`'s top-level siblings; for a single-account
 * connection row they ride alongside the account config in `metadata`. The
 * renderer projection (separate ticket) hoists them to the plugin's top level
 * when assembling the on-box config.
 *
 * @since 1.0.0
 */
export const ExchangeMailAccountConfig = Schema.Struct({
  imap: ExchangeMailImapConfig,
  smtp: ExchangeMailSmtpConfig,
  mailbox: Schema.optional(Schema.NonEmptyTrimmedString),
  rules: Schema.optional(ExchangeMailRules),
  telemetry: Schema.optional(ExchangeMailTelemetry),
});
export type ExchangeMailAccountConfig = Schema.Schema.Type<typeof ExchangeMailAccountConfig>;

/**
 * Upsert an exchange-mail connection keyed on `(installationId, provider,
 * accountKey)`. The non-secret account config lands in `metadata`; `imapPassword`
 * / `smtpPassword` are encrypted into the row's `secretFields` envelope (the
 * exact CLA-745 shape `decryptSecretFields` reads back) and NEVER written to
 * `metadata` or logged. `provider` is fixed to `"exchange-mail"` on the wire.
 *
 * @since 1.0.0
 */
export const UpsertEmailConnectionInput = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
  provider: Schema.Literal("exchange-mail"),
  accountKey: Schema.optional(Schema.String),
  status: Schema.optional(ConnectionStatus),
  accountLabel: Schema.optional(Schema.NullOr(Schema.String)),
  config: ExchangeMailAccountConfig,
  imapPassword: Schema.NonEmptyTrimmedString,
  smtpPassword: Schema.NonEmptyTrimmedString,
});
export type UpsertEmailConnectionInput = Schema.Schema.Type<typeof UpsertEmailConnectionInput>;

/**
 * Delete a connection by id.
 *
 * @since 1.0.0
 */
export const DeleteInstallationConnectionInput = Schema.Struct({
  id: Schema.String,
});

/**
 * Scope a connection mutation (revoke / reconnect) to a single
 * `(installationId, provider, accountKey)` triple. `accountKey` defaults to
 * the empty string for single-account providers. Looking the row up *within*
 * the installation is what keeps the mutation agent-scoped: a connection that
 * belongs to a different installation can never be addressed through here.
 *
 * @since 1.0.0
 */
export const ConnectionScopeInput = Schema.Struct({
  installationId: Schema.String,
  provider: Schema.NonEmptyTrimmedString,
  accountKey: Schema.optional(Schema.String),
});
export type ConnectionScopeInput = Schema.Schema.Type<typeof ConnectionScopeInput>;

/**
 * Per-provider connect status for an installation: whether the provider was
 * picked during intake (`intakeSelected`) versus whether a live connection
 * actually exists and is healthy (`connected`). `status` carries the stored
 * `ConnectionStatus` of a representative row (a connected row is preferred),
 * or `null` when the installation has no row for that provider yet. `kind`
 * carries the catalog entry's auth kind (`oauth` vs `apiKey`) so a UI can pick
 * the right connect affordance for every provider without a second catalog call.
 * `accountLabel` carries the representative row's stored label (for Discord the
 * guild name captured at OAuth), so the UI can render "Connected to **X**";
 * `null` when the representative row has no label or no row exists yet.
 * `driveAccess` / `driveItemCount` are present only for the `google` entry when
 * its representative row's metadata records a Drive access mode (CLA
 * drive-access) — the web Google screen reads them to decide "show the picker"
 * vs "connected"; both are absent for every other provider and for a google row
 * with no `metadata.drive`.
 *
 * @since 1.0.0
 */
export const ConnectionConnectStatus = Schema.Struct({
  key: Schema.String,
  type: ConnectionType,
  kind: Schema.Literal("oauth", "apiKey", "pairing"),
  intakeSelected: Schema.Boolean,
  connected: Schema.Boolean,
  status: Schema.NullOr(ConnectionStatus),
  accountLabel: Schema.NullOr(Schema.String),
  driveAccess: Schema.optional(GoogleDriveAccess),
  driveItemCount: Schema.optional(Schema.Int),
});
export type ConnectionConnectStatus = Schema.Schema.Type<typeof ConnectionConnectStatus>;

/**
 * Connect-status response: one entry per provider in the catalog, intersecting
 * the intake selections with the installation's actual connections.
 *
 * @since 1.0.0
 */
export const ConnectStatusResponse = Schema.Array(ConnectionConnectStatus);

/**
 * Delete acknowledgment.
 *
 * @since 1.0.0
 */
export const DeleteInstallationConnectionResponse = Schema.Struct({
  success: Schema.Boolean,
});

/**
 * Start an admin-authenticated OAuth connect for a `kind: "oauth"` provider on
 * any installation, addressed directly by `installationId`. Mirrors the customer
 * `connections/oauthStart` shape but carries no ownership check — admin role is
 * global (`AdminMiddleware` provides `CurrentActor` as the gate). `surfaces`
 * narrows the requested scopes for providers that expose a scope catalog.
 *
 * @since 1.0.0
 */
export const AdminOAuthStartInput = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
  provider: Schema.NonEmptyTrimmedString,
  surfaces: Schema.optional(Schema.Array(Schema.String)),
});
export type AdminOAuthStartInput = Schema.Schema.Type<typeof AdminOAuthStartInput>;

/**
 * OAuth start response: the provider authorize URL the browser is redirected to.
 *
 * @since 1.0.0
 */
export const AdminOAuthStartResponse = Schema.Struct({ authUrl: Schema.String });

/**
 * Finish an admin-authenticated OAuth connect. The opaque `state` recovers and
 * scopes the exchange inside the shared OAuth machinery (it carries the
 * installation id), so — like the customer rail's callback — no installation id
 * is needed here and no ownership check is possible or required.
 *
 * @since 1.0.0
 */
export const AdminOAuthCallbackInput = Schema.Struct({
  provider: Schema.NonEmptyTrimmedString,
  code: Schema.NonEmptyTrimmedString,
  state: Schema.NonEmptyTrimmedString,
});
export type AdminOAuthCallbackInput = Schema.Schema.Type<typeof AdminOAuthCallbackInput>;

/**
 * OAuth callback response: whether the token exchange + connection persist
 * succeeded.
 *
 * @since 1.0.0
 */
export const AdminOAuthCallbackResponse = Schema.Struct({ success: Schema.Boolean });

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Installation Connection RPC group.
 *
 * All endpoints require admin role via `AdminMiddleware`.
 *
 * @since 1.0.0
 */
export class AdminInstallationConnectionRpcs extends RpcGroup.make(
  Rpc.make("admin.installation.connections.providerCatalog", {
    success: ProviderCatalogResponse,
    error: Schema.Union(Unauthorized, Forbidden),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.list", {
    payload: ListInstallationConnectionsInput,
    success: Schema.Array(InstallationConnection.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.upsert", {
    payload: UpsertInstallationConnectionInput,
    success: InstallationConnection.json,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.upsertEmail", {
    payload: UpsertEmailConnectionInput,
    success: InstallationConnection.json,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, DatabaseError, CryptoError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.delete", {
    payload: DeleteInstallationConnectionInput,
    success: DeleteInstallationConnectionResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.connectStatus", {
    payload: ListInstallationConnectionsInput,
    success: ConnectStatusResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.revoke", {
    payload: ConnectionScopeInput,
    success: InstallationConnection.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.reconnect", {
    payload: ConnectionScopeInput,
    success: InstallationConnection.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.oauthStart", {
    payload: AdminOAuthStartInput,
    success: AdminOAuthStartResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.oauthCallback", {
    payload: AdminOAuthCallbackInput,
    success: AdminOAuthCallbackResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden),
  }).middleware(AdminMiddleware),
  // WhatsApp QR-pairing rail (CLA-707). Mirrors the customer
  // `connections/whatsapp*` verbs but under the global `AdminMiddleware` gate,
  // addressing the installation directly by id (no per-resource ownership
  // check) — the same admin-rail precedent the OAuth verbs above set. Schemas
  // are shared with the customer group via `../whatsapp-pairing.rpc.ts`.
  Rpc.make("admin.installation.connections.whatsappStartPairing", {
    payload: WhatsappPairingInput,
    success: WhatsappStartPairingResponse,
    error: Schema.Union(WhatsappPairingRpcError, Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.whatsappPollPairing", {
    payload: WhatsappPairingInput,
    success: WhatsappPollPairingResponse,
    error: Schema.Union(WhatsappPairingRpcError, Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.installation.connections.whatsappCancelPairing", {
    payload: WhatsappPairingInput,
    success: WhatsappCancelPairingResponse,
    error: Schema.Union(WhatsappPairingRpcError, Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
